// Buyer notes and the commercial terms of the event (Lane 1322). Notes are
// internal: Wingman reads them to propose conditions such as "keep these
// together", but a note never becomes a rule by itself. Terms and the
// supplier description are what suppliers will read once the event is
// published.
import React, { useCallback, useEffect, useRef, useState } from 'react';
// The field list and which terms show are shared with the Overview's Terms card
// (eventTerms.js, Lane 1387); the documents moved to their own tab (DocumentsTab).
import { formatDate, STALE_MESSAGE } from './sourcingApi';
import { DELIVERY_NOTES_LABEL, DESCRIPTION_LABEL, quotesDueText, shownTerms as termsOnScreen,
  termFields } from './eventTerms';

export default function NotesTermsTab({ api, eventId, summary, onChanged, onOpenRound }) {
  const notesEditable = ['draft', 'open', 'closed'].includes(summary.status);
  // After publish the terms are edited on the change in progress (slice 2E).
  const { amendment } = termsOnScreen(summary);
  // The saved terms object itself (never a fresh {}), so the effect below runs only when
  // the saved terms change, exactly as before.
  const shownTerms = amendment ? amendment.terms : summary.terms;
  const termsEditable = summary.status === 'draft' || Boolean(amendment);
  // After publish the round's due time is the only deadline (PLAN 8.1): the
  // proposed due date in the terms is never shown again.
  const published = summary.status !== 'draft';
  const round = summary.round || {};
  const fields = termFields(summary);
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [terms, setTerms] = useState(shownTerms || {});
  // Load errors are kept apart from action errors, so the reload that follows
  // a refused (stale) change cannot wipe the message about it.
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saved, setSaved] = useState('');
  // Lane 1438: terms typed here and not saved yet stay on screen when the event reloads
  // (Review and publish saves as the buyer types, which reloads the event). The fields the
  // buyer changed are put on top of the new saved terms, so a save never sends back an
  // old value of a field changed elsewhere (the offers due date, for example).
  const termsDirty = useRef(false);
  const baseTerms = useRef(shownTerms || {});

  const load = useCallback(() => {
    api.notes(eventId).then((r) => { setNotes(r.notes); setLoadError(''); }).catch((e) => setLoadError(e.message));
  }, [api, eventId]);
  // summary.revision: a note added from chat shows up here too.
  useEffect(() => { load(); }, [load, summary.revision]);
  useEffect(() => {
    const server = shownTerms || {};
    const base = baseTerms.current;
    baseTerms.current = server;
    // Not editable any more (published, or a change was published): the saved terms
    // only, never an unsaved overlay under "Suppliers see these terms".
    if (!termsEditable) termsDirty.current = false;
    if (!termsDirty.current) { setTerms(server); return; }
    setTerms((local) => {
      const next = { ...server };
      new Set([...Object.keys(local), ...Object.keys(base)]).forEach((k) => {
        if (String(local[k] ?? '') === String(base[k] ?? '')) return;   // not changed here
        if (local[k] === undefined || local[k] === '') delete next[k];
        else next[k] = local[k];
      });
      return next;
    });
  }, [shownTerms, termsEditable]);
  const editTerms = (next) => { termsDirty.current = true; setSaved(''); setTerms(next); };

  const run = async (fn) => {
    setError('');
    try { await fn(); await onChanged(); load(); return true; } catch (e) {
      if (e.code === 'stale_revision') await onChanged();
      setError(e.code === 'stale_revision' ? STALE_MESSAGE : e.message);
      return false;
    }
  };

  const saveTerms = async () => {
    const clean = {};
    Object.entries(terms).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      clean[k] = k === 'offer_validity_days' ? Number(v) : v;
    });
    if (await run(async () => {
      await api.act(eventId, 'PUT', '/terms', { terms: clean }, summary.revision);
      termsDirty.current = false;   // the reload that follows shows the saved terms
    })) setSaved('Terms saved.');
  };

  return (
    <div className="srce-two">
      <section>
        <h2 className="srce-h2">Notes</h2>
        <div className="srce-mut srce-gap">Only your team sees notes. Wingman reads them when it suggests conditions, and you confirm any condition before it is used.</div>
        {error && <div className="srce-error" role="alert">{error}</div>}
        {loadError && <div className="srce-error" role="alert">{loadError}</div>}
        {notes && notes.length === 0 && <div className="srce-mut">No notes yet.</div>}
        {notes && notes.map((n) => (
          <div key={n.note_id} className="srce-note">
            {editing === n.note_id ? (
              <>
                <textarea className="srce-textarea" rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} />
                <div className="srce-row">
                  <button type="button" className="srce-btn srce-btn--primary" disabled={!editText.trim()}
                    onClick={async () => { if (await run(() => api.act(eventId, 'PATCH', `/notes/${n.note_id}`, { body: editText.trim() }, summary.revision))) setEditing(null); }}>Save</button>
                  <button type="button" className="ll-clr" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="srce-note-body">{n.body}</div>
                <div className="srce-sub">{n.author_name || 'A buyer'}, {formatDate(n.updated_at)}
                  {notesEditable && (
                    <>
                      {' '}<button type="button" className="ll-clr" onClick={() => { setEditing(n.note_id); setEditText(n.body); }}>Edit</button>
                      <button type="button" className="ll-clr" onClick={() => run(() => api.actQuery(eventId, 'DELETE', `/notes/${n.note_id}`, summary.revision))}>Delete</button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        {notesEditable && (
          <div className="srce-gap">
            <textarea className="srce-textarea" rows={3} value={draft} maxLength={8000}
              placeholder="For example: the 2-2xx sizes should stay with one supplier." aria-label="New note"
              onChange={(e) => setDraft(e.target.value)} />
            <button type="button" className="srce-btn srce-btn--primary" disabled={!draft.trim()}
              onClick={async () => { if (await run(() => api.act(eventId, 'POST', '/notes', { body: draft.trim() }, summary.revision))) setDraft(''); }}>Add note</button>
          </div>
        )}
      </section>
      <section>
        <h2 className="srce-h2">Terms</h2>
        <div className="srce-mut srce-gap">{amendment ? 'You are editing the change in progress. Suppliers see these terms after you publish the change.'
          : published ? 'Suppliers see these terms. To change them, start a change.' : 'Suppliers see these when the event is published.'}</div>
        {published && (
          <div className="srce-field">
            <span className="srce-label">Quotes due</span>
            <span className="srce-row">
              <span>{quotesDueText(summary)}</span>
              {onOpenRound && summary.status === 'open' && !round.closed_at && (
                <button type="button" className="ll-clr" onClick={onOpenRound}>Extend</button>
              )}
            </span>
          </div>
        )}
        {fields.map(([key, label, placeholder]) => (
          <div key={key} className="srce-field">
            <label className="srce-label" htmlFor={`srce-t-${key}`}>{label}</label>
            <input id={`srce-t-${key}`} className="srce-input srce-input--wide" disabled={!termsEditable}
              type={key === 'due_date' ? 'date' : 'text'} value={terms[key] ?? ''} placeholder={placeholder}
              onChange={(e) => editTerms({ ...terms, [key]: e.target.value })} />
          </div>
        ))}
        <div className="srce-field">
          <label className="srce-label" htmlFor="srce-t-desc">{DESCRIPTION_LABEL}</label>
          <textarea id="srce-t-desc" className="srce-textarea" rows={5} disabled={!termsEditable}
            value={terms.supplier_description ?? ''} maxLength={20000}
            onChange={(e) => editTerms({ ...terms, supplier_description: e.target.value })} />
        </div>
        <div className="srce-field">
          <label className="srce-label" htmlFor="srce-t-deliv">{DELIVERY_NOTES_LABEL}</label>
          <textarea id="srce-t-deliv" className="srce-textarea" rows={3} disabled={!termsEditable}
            value={terms.delivery_notes ?? ''} maxLength={4000}
            onChange={(e) => editTerms({ ...terms, delivery_notes: e.target.value })} />
        </div>
        {termsEditable && (
          <div className="srce-actions">
            <button type="button" className="srce-btn srce-btn--primary" onClick={saveTerms}>Save terms</button>
            {saved && <span className="srce-good">{saved}</span>}
          </div>
        )}
      </section>
    </div>
  );
}
